import { useStacklineTheme } from '@stackline/ui';
import React, { useState, useCallback } from 'react';
import {
  BeaconProSidebarFilters,
  ICON_STYLES
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Filters/constants';
import CheckboxItem from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/CheckboxItem';
import GenericSidebarFilter, {
  GenericSidebarFilterProps
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/GenericSidebarFilter';
import {
  FILTER_OPERATION,
  useSavedSearchBuilder
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/hooks/useSavedSearchBuilder';
import { SlSidebarScrollableContainer } from 'src/components/BeaconV2Components/SlSidebar';
import { SlExcludeSelectedCheckbox, SlSelectedCheckbox } from 'src/components/SvgIcons';
import { useAppSelector } from 'src/utils/Hooks';

const BrandSidebarFilter = (props: GenericSidebarFilterProps) => {
  const theme = useStacklineTheme();
  const { formData, updateSavedSearch } = useSavedSearchBuilder();
  /**
   * Toggle mode for include/exclude; determines which icon to render in the renderItems function.
   */
  const isExcluded = formData.termFilters?.excludedBrandId.values.length > 0;
  const [toggleMode, setToggleMode] = useState<'include' | 'exclude'>(isExcluded ? 'exclude' : 'include');
  const allFollowingBrands = useAppSelector((state) => state.brandsFollowing).filter((brand) => brand?.id !== 0);

  const [selectedBrands, setSelectedBrands] = useState<Array<{ i: string; n?: string }>>(
    formData.termFilters?.brandId.values.length
      ? formData.termFilters?.brandId.values
      : formData.termFilters?.excludedBrandId.values.length
        ? formData.termFilters?.excludedBrandId.values
        : []
  );

  const brandOptions = allFollowingBrands.map((brand) => ({
    ...brand,
    selected: selectedBrands.some((selectedBrand) => Number(selectedBrand.i) === (brand.id as number))
  }));

  /**
   * Search input for filtering down brands
   */
  const [currentInput, setCurrentInput] = useState<string>('');

  const getIcon = useCallback(() => {
    switch (toggleMode) {
      case 'include': {
        return <SlSelectedCheckbox style={ICON_STYLES} />;
      }

      case 'exclude': {
        return <SlExcludeSelectedCheckbox style={ICON_STYLES} />;
      }
      default:
        return null;
    }
  }, [toggleMode]);

  /**
   * Handles the selection of a brand.
   */
  const handleItemSelect = useCallback(
    ({ value }: { value: { id: number; name: string; selected: boolean } }) => {
      if (value.selected) {
        setSelectedBrands((prev) => {
          return prev.filter((selectedBrand) => +selectedBrand.i !== +value.id);
        });
      } else {
        setSelectedBrands((prev) => {
          return [...prev, { n: value.name, i: value.id }];
        });
      }

      if (toggleMode === 'include') {
        updateSavedSearch({
          key: BeaconProSidebarFilters.Brand.filterKey,
          newValue: String(value.id),
          operation: FILTER_OPERATION.Toggle
        });
      } else {
        updateSavedSearch({
          key: BeaconProSidebarFilters.ExcludedBrand.filterKey,
          newValue: String(value.id),
          operation: FILTER_OPERATION.Toggle
        });
      }
    },
    [toggleMode, updateSavedSearch]
  );
  /**
   * Renders the list of items as checkboxes.
   */
  const renderItems = useCallback(() => {
    return (
      <SlSidebarScrollableContainer>
        {brandOptions
          ?.filter((brand) => brand.name.toLowerCase().includes(currentInput.toLowerCase()))
          .sort((a, b) => (a.selected === b.selected ? 0 : a.selected ? -1 : 1)) // sort by checked items
          .map((brand, _index) => {
            return (
              <CheckboxItem
                onClick={() =>
                  handleItemSelect({ value: { id: brand.id, name: brand.name, selected: brand.selected } })
                }
                value={brand.id}
                key={brand.id}
                label={brand.name}
                theme={theme}
                checked={brand.selected}
                checkedIcon={getIcon()}
              />
            );
          })}
      </SlSidebarScrollableContainer>
    );
  }, [brandOptions, currentInput, theme, getIcon, handleItemSelect]);

  /**
   * Handles the change of the input to filter down the brands.
   */

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentInput(e.target.value);
  };

  /**
   * Clears the selected brands
   */
  const handleClear = () => {
    updateSavedSearch({
      key: BeaconProSidebarFilters.Brand.filterKey,
      callback: (form) => {
        form.termFilters[BeaconProSidebarFilters.Brand.filterKey].values = [];
        form.termFilters[BeaconProSidebarFilters.ExcludedBrand.filterKey].values = [];
        return form;
      },
      operation: null,
      newValue: null
    });
    setSelectedBrands([]);
    setCurrentInput('');
  };

  const handleToggle = (toggle: 'include' | 'exclude') => {
    setToggleMode(toggle);
    if (toggle === 'include') {
      updateSavedSearch({
        key: BeaconProSidebarFilters.Brand.filterKey,
        callback: (form) => {
          form.termFilters[BeaconProSidebarFilters.Brand.filterKey].values = selectedBrands.map((brand) => ({
            i: brand.i,
            n: brand.n
          }));
          form.termFilters[BeaconProSidebarFilters.ExcludedBrand.filterKey].values = [];

          return form;
        },
        operation: null,
        newValue: null
      });
    } else {
      updateSavedSearch({
        key: BeaconProSidebarFilters.ExcludedBrand.filterKey,
        callback: (form) => {
          form.termFilters[BeaconProSidebarFilters.ExcludedBrand.filterKey].values = selectedBrands.map((brand) => ({
            i: brand.i,
            n: brand.n
          }));
          form.termFilters[BeaconProSidebarFilters.Brand.filterKey].values = [];

          return form;
        },
        operation: null,
        newValue: null
      });
    }
  };

  return (
    <GenericSidebarFilter
      {...props}
      toggleMode={toggleMode}
      renderItems={renderItems}
      handleClear={handleClear}
      handleToggle={handleToggle}
      includeClear={selectedBrands?.length > 0}
      sidebarInputProps={{
        ...props.sidebarInputProps,
        value: currentInput,
        onChange: handleChange
      }}
    />
  );
};

export default BrandSidebarFilter;
